/**
 * SubmissionSection — one grouped, collapsible section per submission on the
 * weekly rollups tab.
 *
 * Header row (2026-07-28 redesign, client; flattened 2026-08-01): one line —
 * the submission number as a mono heading, the worked span, then a
 * right-anchored run of total · MOD/FWA · edit control. The slots have
 * min-widths so stacked submissions inside a group line up as columns, which
 * is what makes a second open submission findable at a glance. The number
 * drops the "PCO" prefix because the page header carries it. Expanded, the
 * row grows the edit control (red chip while a reference is missing, quiet
 * pencil once both are on the row), plus the MOD/FWA edit fields or the
 * comment toggle, a "Mark Approved" action for closed submissions, and the
 * submission's member weekly rollups (passed as children).
 *
 * No status chip here on purpose (client, 2026-07-28): this component only
 * ever renders inside WeeklyRollupsTab's Open / Sent / Approved group cards,
 * so the chip restated the card it was sitting in. It carried no aria or
 * screen-reader-only semantics — it was plain visible text — so the group
 * heading is now the single, unduplicated statement of status. If this
 * component is ever reused OUTSIDE a status-grouped container, the status has
 * to come back.
 *
 * Inline edit: submission number is editable while the submission is open, and
 * also on a LEDGER-ONLY historical row whatever its status (closed/approved
 * numbers are otherwise locked, per SubmissionPatchRequest — but a ledger row
 * has no sent xlsx or cover email behind it, so the lock's premise doesn't
 * hold). MOD, FWA and comments are always editable. Closed submissions get a
 * "Mark Approved" action (closed → approved). THE approval action at the T&M
 * level (client ruling 2026-07-29): the submission is the unit, and the server
 * cascades it to the member weeks, so there is no per-week approve button any
 * more. Orange means we asked; green means they said yes.
 *
 * Ledger-only rows (pre-portal history: an imported total and zero member
 * weeks) additionally get their AMOUNT and WORKED RANGE in the same editor,
 * plus a delete. Both exist because that history is now PM-entered — a
 * mistyped amount or a package entered twice has to be fixable by the person
 * who typed it, and neither is reachable any other way.
 *
 * An EMPTY PACKAGE (2026-08-24) gets the other delete: open, no weeks, not the
 * PCO's collecting default, no imported total — the package a first saved
 * block auto-opened, left behind when the real number was started after it and
 * the week followed. It sits expanded
 * under the row as a danger-colored text action — one step QUIETER than
 * RULES 2.7's default destructive control (`.btn-danger`, which the ledger
 * "Delete row" beside it uses), and a deliberate deviation from it: an empty
 * package holds no weeks, no money and no send record, so there is nothing to
 * warn about beyond the number going away. Same treatment a hand-added inbox
 * line uses to remove itself (`inbox/InboxLaborTable.tsx`,
 * `inbox/InboxEquipmentTable.tsx`). The rule for WHEN it renders is
 * `canRemoveEmptyPackage` (`lib/emptyPackage.ts`), which mirrors the server's
 * guards so the action is never offered on a package the API would refuse.
 */
import { useState } from 'react';
import { SquarePen, X } from 'lucide-react';
import { normalizeModNumber } from '@coffman-tm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useToast } from './ToastProvider';
import { ConfirmModal } from './ConfirmModal';
import { MoneyInput } from './MoneyInput';
import { ExpandChevron } from './design';
import {
  formatMoneyCents,
  formatSubmissionLabel,
  formatSubmissionShort,
  formatWeekRangeSpanLong,
  gcName,
} from '../lib/format';
import { submissionErrorMessage } from '../lib/useSubmissions';
import { canRemoveEmptyPackage } from '../lib/emptyPackage';
import { parseAmountCents } from './pco/BucketRowEditor';
import type { RollupListItem, SubmissionSummary } from '@coffman-tm/shared';

function rangeLabel(s: SubmissionSummary): string {
  if (!s.week_range) {
    // Imported labels arrive as Josh/Joey typed them ("5.9.26 - 5.16.26",
    // "3.21.26") and sat beside computed spans ("5/17 – 6/6") in two formats.
    // Normalize dotted dates to the app's slash form for DISPLAY only — the
    // stored label is untouched. Current-year years drop ("5/9 – 5/16");
    // other years keep a 2-digit year so old history stays honest
    // ("11/19/25 – 12/31/25"). Anything unparseable renders verbatim.
    if (s.imported_range_label) return normalizeImportedRange(s.imported_range_label);
    // Ledger-only backfill submission with no label at all.
    return 'No weeks yet';
  }
  // Full worked span: the first member week's Sunday through the last week's
  // Saturday ("Jun 21 – Jul 4"); a single week reads "Jun 21 – Jun 27".
  // Month names, not M/D (2026-08-05) — the numeric form read as arithmetic
  // sitting next to a column of money. Imported labels above keep their own
  // shape: those are the PCO Log's own text, and restyling them would claim
  // the log says something it doesn't.
  const { first_week, last_week } = s.week_range;
  return formatWeekRangeSpanLong(first_week, last_week);
}

/** "5.9.26" → "5/9" (current year) or "11.19.25" → "11/19/25" (any other
 *  year). Returns null when the piece isn't a dotted date. */
function normalizeDottedDate(piece: string): string | null {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(piece.trim());
  if (!m) return null;
  const [, mo, d, y] = m;
  const yy = y!.length === 4 ? y!.slice(2) : y!;
  const currentYy = String(new Date().getFullYear() % 100);
  return yy === currentYy ? `${Number(mo)}/${Number(d)}` : `${Number(mo)}/${Number(d)}/${yy}`;
}

/** Display-only cleanup of a hand-typed imported range label. Both sides (or
 *  the single date) must parse as dotted dates, else the label renders
 *  verbatim — never half-translate a string a human wrote. */
function normalizeImportedRange(label: string): string {
  const parts = label.split(/[-–]/).map((p) => p.trim());
  if (parts.length === 1) {
    return normalizeDottedDate(parts[0]!) ?? label;
  }
  if (parts.length === 2) {
    const a = normalizeDottedDate(parts[0]!);
    const b = normalizeDottedDate(parts[1]!);
    if (a && b) return `${a} – ${b}`;
  }
  return label;
}

export function SubmissionSection({
  pcoParentNumber,
  submission,
  defaultExpanded,
  onChanged,
  children,
}: {
  pcoParentNumber: string;
  submission: SubmissionSummary;
  /** Mount-time default only — deliberately not re-derived on every render.
   *  A post-send refetch must not slam an already-open section shut under
   *  the user's cursor, so this is read once via useState's initializer and
   *  never synced afterward. */
  defaultExpanded: boolean;
  onChanged: () => Promise<void>;
  children?: React.ReactNode;
}) {
  const { token, me } = useAuth();
  const toast = useToast();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState(submission.submission_number);
  const [modNumber, setModNumber] = useState(submission.mod_number ?? '');
  const [fwa, setFwa] = useState(submission.hoffman_fwa ?? '');
  const [comments, setComments] = useState(submission.comments ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveEmpty, setConfirmRemoveEmpty] = useState(false);
  const [showComments, setShowComments] = useState(false);

  /** No MOD or no FWA — the row is still chasing its references, which is what
   *  the red Edit chip is for. Both present and the control goes quiet. */
  const missingRefs = !submission.mod_number || !submission.hoffman_fwa;

  /**
   * A ledger-only row: an imported total with no member weeks behind it. That
   * is the exact predicate the server uses for the amount/range patch carve-out
   * and for DELETE, so the editor shows those controls on precisely the rows
   * that accept them. (A submission that carries an imported total AND weeks is
   * NOT one — member-rollups-win means the derive ignores its imported total,
   * so editing it would look like a correction that moved no money.)
   */
  const isLedgerOnly =
    submission.imported_total_cents != null && submission.rollups.length === 0;
  const numberEditable = submission.status === 'open' || isLedgerOnly;
  /** Does this package have member weeks to head a table with? */
  const hasWeeks = submission.rollups.length > 0;
  /**
   * The orphaned shell: open, no weeks, not the PCO's collecting default, no
   * imported total. Same four conditions the server deletes on — see
   * `lib/emptyPackage.ts` for why each one is there.
   */
  const removableEmpty = canRemoveEmptyPackage(submission);

  const [amountDollars, setAmountDollars] = useState(
    submission.imported_total_cents != null
      ? (submission.imported_total_cents / 100).toFixed(2)
      : '',
  );
  const [importedRange, setImportedRange] = useState(submission.imported_range_label ?? '');

  const toggleEdit = (e: React.MouseEvent) => {
    // The header row itself toggles expand/collapse on click — stop that
    // from also firing when the Edit/Cancel link inside it is clicked.
    e.stopPropagation();
    if (editing) {
      setEditing(false);
    } else {
      startEdit();
    }
  };

  const startEdit = () => {
    // Reset the fields to the current submission before opening the editor so a
    // prior cancel doesn't leave stale input.
    setNumber(submission.submission_number);
    setModNumber(submission.mod_number ?? '');
    setFwa(submission.hoffman_fwa ?? '');
    setComments(submission.comments ?? '');
    setAmountDollars(
      submission.imported_total_cents != null
        ? (submission.imported_total_cents / 100).toFixed(2)
        : '',
    );
    setImportedRange(submission.imported_range_label ?? '');
    setEditing(true);
  };

  const saveEdits = async () => {
    if (!token) return;
    // The server refuses a non-positive imported total (credits are out of
    // scope in v1); catching it here keeps the reason next to the field.
    if (isLedgerOnly && parseAmountCents(amountDollars) <= 0) {
      toast.push({
        variant: 'error',
        title: 'Save failed',
        body: 'Enter an amount greater than zero.',
      });
      return;
    }
    setBusy(true);
    try {
      await api.submissions.patch(token, submission.id, {
        ...(numberEditable && number.trim() !== submission.submission_number
          ? { submission_number: number.trim() }
          : {}),
        mod_number: modNumber.trim() || null,
        hoffman_fwa: fwa.trim() || null,
        comments: comments.trim() || null,
        ...(isLedgerOnly
          ? {
              imported_total_cents: parseAmountCents(amountDollars),
              imported_range_label: importedRange.trim() || null,
            }
          : {}),
      });
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'error',
        title: 'Save failed',
        body: submissionErrorMessage(e, 'Could not update.', gcName(me)),
      });
    } finally {
      setBusy(false);
    }
  };

  const markApproved = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.submissions.patch(token, submission.id, { status: 'approved' });
      toast.push({
        variant: 'success',
        title: 'Approved to bill',
        body: `${formatSubmissionLabel(pcoParentNumber, submission.submission_number)} marked approved.`,
        durationMs: 6000,
      });
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'error',
        title: 'Failed',
        body: submissionErrorMessage(e, 'Could not update.', gcName(me)),
      });
    } finally {
      setBusy(false);
    }
  };

  // Packages normally close through Approve & Send: the confirm step on the
  // last unsent week offers it. This button is the escape hatch for the one
  // state that path can't reach — everything sent, checkbox not ticked (or the
  // week was sent from its own detail page, which has no offer). Then the
  // package sits Open forever with nothing left to send.
  //
  // Gate: open, has weeks, and none of them is still in_progress. That is
  // exactly the server's `unsent_weeks_exist` guard, so the button is never
  // shown for a close that would be refused. It deliberately does NOT appear
  // while any week is unsent (the checkbox owns that path) nor on a brand-new
  // package with no weeks at all — which is the collecting one, and closing it
  // would leave the PCO collecting nowhere. In steady state it is invisible.
  const canClose =
    submission.status === 'open' &&
    submission.rollups.length > 0 &&
    submission.rollups.every((r) => r.status !== 'in_progress');

  const closeSubmission = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.submissions.close(token, submission.id);
      toast.push({
        variant: 'success',
        title: 'Submission closed',
        body: `${formatSubmissionLabel(pcoParentNumber, submission.submission_number)} is closed.`,
        durationMs: 6000,
      });
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'error',
        title: 'Could not close',
        body: submissionErrorMessage(e, 'Could not close this submission.', gcName(me)),
      });
    } finally {
      setBusy(false);
    }
  };

  // The only delete of a submission anywhere in the app, and it reaches the
  // two shapes with nothing behind them — a ledger row (pre-portal history
  // entered twice) and an empty package (the orphaned shell). Neither owns a
  // week, a block, a PDF or a send record, so removing one destroys nothing
  // but the row. One call site for both: the server decides the shape, and a
  // second copy of this handler would be a second place for the error path to
  // drift.
  const removeSubmission = async (kind: 'ledger' | 'empty') => {
    if (!token) return;
    setBusy(true);
    try {
      // The SERVER decides which shape it removed (it re-reads the row), so the
      // confirmation reports what actually happened rather than which button
      // was pressed. `kind` still picks the failure copy — on an error there is
      // no response to read.
      const res = await api.submissions.remove(token, submission.id);
      setConfirmDelete(false);
      setConfirmRemoveEmpty(false);
      const label = formatSubmissionLabel(pcoParentNumber, submission.submission_number);
      toast.push({
        variant: 'success',
        title: 'Removed',
        body: res.shape === 'ledger' ? `${label} is off the ledger.` : `${label} is gone.`,
        durationMs: 6000,
      });
      await onChanged();
    } catch (e) {
      // Close the confirm on failure too: a refused delete left it open,
      // inviting a second click on a button that will refuse again. The toast
      // carries the reason, and the row is still there to try from.
      setConfirmDelete(false);
      setConfirmRemoveEmpty(false);
      toast.push({
        variant: 'error',
        title: 'Could not remove',
        body: submissionErrorMessage(
          e,
          kind === 'ledger' ? 'Could not remove this row.' : 'Could not remove this package.',
          gcName(me),
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Stack item, not a card — it lives inside the Packages card on the
    // Weekly Rollups tab. Row rule between siblings; the card provides the
    // chrome.
    <div className="sub-stack-item">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          // Column gutter. Wide enough that the slots read as separate facts
          // rather than one run-on line.
          columnGap: 20,
          rowGap: 6,
          flexWrap: 'wrap',
          cursor: 'pointer',
          padding: '13px 0',
        }}
      >
        {/* Heading slot — chevron + "004.13". The page header above already
            says which PCO this is, so the row leads with the submission
            number alone. Fixed-width so sibling rows in a group align. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            minWidth: 110,
            flexShrink: 0,
          }}
        >
          <ExpandChevron expanded={expanded} />
          {/* Sans, not mono (Brandon 2026-08-05: "we overuse that" — the mono
              voice stays on money and code-like values, not headings). */}
          <span
            style={{
              fontSize: 15.5,
              fontWeight: 600,
              color: 'var(--ink)',
            }}
          >
            {formatSubmissionShort(pcoParentNumber, submission.submission_number)}
          </span>
        </div>

        {/* Worked span slot — same rank as the submission number (client,
            2026-08-01): with two open submissions the date span is how the PM
            tells 004.13 from 004.14 at a glance, so it reads as identity, not
            meta. */}
        <span
          style={{
            fontSize: 15.5,
            fontWeight: 600,
            color: 'var(--ink)',
            minWidth: 92,
          }}
        >
          {rangeLabel(submission)}
        </span>
        {/* The references this package bills under, inline after the span it
            covers (2026-08-05). They used to sit on the far right beside the
            money, which read them as a second figure; in the small-cap voice
            here they read as what they are — the package's identity to the
            GC. Rendered whenever either exists; the edit control on the right
            is what fills them when they don't. */}
        {(submission.mod_number || submission.hoffman_fwa) && (
          <span className="eyebrow" style={{ letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
            {submission.mod_number ? `MOD ${submission.mod_number}` : null}
            {submission.mod_number && submission.hoffman_fwa ? ' / ' : null}
            {submission.hoffman_fwa}
          </span>
        )}
        {/* A COLLAPSED row with missing references still has to say so — the
            red chip below only renders expanded, and before the refs moved
            inline the old "MOD — / FWA —" placeholders were the tell. Same
            quiet amber voice as the bucket row's "needs a reference". */}
        {!expanded && missingRefs && (
          <span style={{ fontSize: 11, color: 'var(--warning)', whiteSpace: 'nowrap' }}>
            needs MOD/FWA
          </span>
        )}
        {/* The edit control sits WITH the references it edits (Brandon
            2026-08-05, restoring the 2026-07-28 pairing) — beside them when
            they exist, in their place when they don't.

            STATE-DEPENDENT since 2026-08-01 (client revision). Missing MOD
            or FWA keeps the red chip — red here is attention ("fix the
            references"), NOT the destructive red used elsewhere in this app.
            Once both references are on the row there is nothing to chase, so
            it drops to the quiet pencil every other row-level edit in the app
            uses. See .sub-edit-btn in index.css before changing either. */}
        {expanded &&
          (missingRefs ? (
            <button
              type="button"
              className="sub-edit-btn"
              disabled={busy}
              onClick={toggleEdit}
              // Enter/Space here would otherwise bubble to the header's own
              // onKeyDown (which unconditionally toggles expand/collapse on
              // those keys) before the button's native activation fires —
              // stopping propagation at keydown, not just click, keeps
              // keyboard activation of this nested button working.
              onKeyDown={(e) => e.stopPropagation()}
            >
              {/* The chip names the gap, not the gesture (client,
                  2026-08-02): a red pill reading "Edit" said nothing about
                  WHY it was red. */}
              {editing ? 'Cancel' : 'Add MOD/FWA'}
            </button>
          ) : (
            <button
              type="button"
              className="icon-btn muted"
              style={{ alignSelf: 'center' }}
              disabled={busy}
              onClick={toggleEdit}
              onKeyDown={(e) => e.stopPropagation()}
              title={editing ? 'Cancel' : 'Edit MOD/FWA'}
              aria-label={editing ? 'Cancel' : 'Edit MOD/FWA'}
            >
              {editing ? <X size={14} aria-hidden /> : <SquarePen size={14} aria-hidden />}
            </button>
          ))}
        {/* Progress on an OPEN package carrying sent weeks (client,
            2026-08-02): the Sent pile means "whole package delivered", so an
            open package says its partial truth here — "2 of 3 weeks sent" —
            instead of its sent weeks migrating piles. Silent when nothing has
            gone out yet. */}
        {submission.status === 'open' &&
          (() => {
            const weeks = submission.rollups ?? [];
            const sent = weeks.filter((r) => r.status !== 'in_progress').length;
            if (sent === 0 || weeks.length === 0) return null;
            return (
              <span style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                {sent} of {weeks.length} week{weeks.length === 1 ? '' : 's'} sent
              </span>
            );
          })()}

        {/* Right-hand run — the money alone now (the edit control moved up
            beside the references it edits). Pushed to the far end of the row
            so the dollar figures of stacked submissions form a column. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 16,
            marginLeft: 'auto',
          }}
        >
          {/* Headline sans at the row's own size, not the table mono — the
              package total has to outrank the week tables' mono Subtotal
              column below it (2026-08-06). */}
          <span
            style={{
              fontSize: 15.5,
              fontWeight: 600,
              color: 'var(--ink)',
              minWidth: 132,
              textAlign: 'right',
            }}
          >
            {formatMoneyCents(
              submission.total_cents !== 0
                ? submission.total_cents
                : submission.imported_total_cents ?? 0,
            )}
          </span>
        </div>
      </div>

      {expanded && (
        // Left rail + indent: the expanded contents are CHILDREN of the row
        // above, and the layout has to say so (client, 2026-08-01 — a
        // single-week package read as a duplicate of its own header because
        // the week rendered at the same visual level).
        <div
          style={{
            marginTop: 10,
            marginLeft: 7,
            paddingLeft: 16,
            borderLeft: '2px solid var(--rule)',
          }}
        >
          {submission.status === 'closed' && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 8 }}>
              <button type="button" className="btn btn-cta" disabled={busy} onClick={markApproved}>
                Mark Approved
              </button>
            </div>
          )}
          {/* The empty shell. Its expanded body is otherwise blank — no weeks,
              no table, nothing — so the line states what the package is and
              the action sits beside it. RULES 2.7's default for a remove is
              `.btn-danger`, and this deliberately goes one step quieter: a
              danger-colored text action, the same treatment a hand-added inbox
              line uses to remove itself (inbox/InboxLaborTable.tsx). The
              deviation is earned by the row's own emptiness — no weeks, no
              money, no send record — not by a rule about what red is for. The
              ledger "Delete row" below keeps the default button. */}
          {removableEmpty && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'baseline',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                marginBottom: 8,
              }}
            >
              <span className="muted" style={{ fontSize: 12.5 }}>
                Nothing has been saved into this package.
              </span>
              <button
                type="button"
                className="btn-link"
                style={{ fontSize: 12.5, color: 'var(--danger)' }}
                disabled={busy}
                onClick={() => setConfirmRemoveEmpty(true)}
                title="Remove this package — it holds no weeks"
              >
                Remove empty package
              </button>
            </div>
          )}
          {/* Ghost, not CTA: this is filing, not the encouraged path — sending
              the last week with the close box ticked is. Same row treatment as
              "Mark Approved". */}
          {canClose && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 8 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={closeSubmission}
                title="Everything here was sent — file this package."
              >
                {busy ? 'Closing…' : 'Close submission'}
              </button>
            </div>
          )}
          {/* sub-edit-row: readable field labels for this row only (see
              index.css) — the eyebrow-sized gray caps were unreadable here. */}
          {editing ? (
            <div
              className="sub-edit-row"
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}
            >
              {numberEditable && (
                <div className="new-pco-field" style={{ minWidth: 120 }}>
                  <label className="field-label">Number</label>
                  <input
                    className="field-input mono"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    disabled={busy}
                  />
                </div>
              )}
              {/* Pre-portal history only: the two facts a ledger row IS. On a
                  rollup-backed submission both are derived from the member
                  weeks and there is nothing here to type. */}
              {isLedgerOnly && (
                <>
                  <div className="new-pco-field" style={{ minWidth: 130 }}>
                    <label className="field-label">Amount</label>
                    <MoneyInput
                      className="field-input mono"
                      value={amountDollars}
                      onChange={setAmountDollars}
                      disabled={busy}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="new-pco-field" style={{ minWidth: 150 }}>
                    <label className="field-label">Date range</label>
                    <input
                      className="field-input"
                      value={importedRange}
                      onChange={(e) => setImportedRange(e.target.value)}
                      disabled={busy}
                      placeholder="3.21.26 - 4.11.26"
                    />
                  </div>
                </>
              )}
              <div className="new-pco-field" style={{ minWidth: 120 }}>
                <label className="field-label">MOD</label>
                <input
                  className="field-input mono"
                  value={modNumber}
                  onChange={(e) => setModNumber(e.target.value)}
                  onBlur={() => {
                    // Reflect the API's storage normalization (zero-padded 3
                    // digits) so the PM sees on-screen what will actually save.
                    const normalized = normalizeModNumber(modNumber) ?? '';
                    if (normalized !== modNumber) setModNumber(normalized);
                  }}
                  disabled={busy}
                />
              </div>
              <div className="new-pco-field" style={{ minWidth: 120 }}>
                {/* Bare "FWA" (client, 2026-07-28) — it matches the header row
                    above it, which has always read "MOD … / FWA …". */}
                <label className="field-label">FWA</label>
                <input
                  className="field-input mono"
                  value={fwa}
                  onChange={(e) => setFwa(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="new-pco-field" style={{ minWidth: 200, flex: '1 1 200px' }}>
                <label className="field-label">Comments</label>
                <input
                  className="field-input"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  disabled={busy}
                />
              </div>
              <button
                type="button"
                className="btn btn-dark"
                disabled={busy}
                onClick={saveEdits}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
              {isLedgerOnly && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                  title="Remove this pre-portal row from the ledger"
                >
                  Delete row
                </button>
              )}
            </div>
          ) : (
            // A submission with no comment says nothing at all now (client,
            // 2026-08-01) — "No comments." was a line of chrome on every row
            // reporting an absence. One that HAS a comment offers it behind a
            // quiet toggle rather than opening it into the stack.
            submission.comments && (
              <div style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  className="btn-link"
                  style={{ fontSize: 12.5 }}
                  aria-expanded={showComments}
                  onClick={() => setShowComments((v) => !v)}
                >
                  {showComments ? 'Hide comment' : '1 comment'}
                </button>
                {showComments && (
                  <p className="muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 0 }}>
                    {submission.comments}
                  </p>
                )}
              </div>
            )
          )}
          {/* The week table's column headers, stated ONCE for the package
              (2026-08-05) — every week row under them carries values only.
              Suppressed on a ledger-only package, which has no weeks to name;
              its own "No weeks yet" line is what renders instead. */}
          {hasWeeks ? (
            <div className="week-table">
              <div className="week-row week-row-head" aria-hidden>
                <span className="eyebrow">Week ending</span>
                <span className="eyebrow week-row-sub">Subtotal</span>
                <span className="eyebrow week-row-actions">Actions</span>
              </div>
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        title="Remove this historical row?"
        body={
          <>
            {formatSubmissionLabel(pcoParentNumber, submission.submission_number)} —{' '}
            {formatMoneyCents(submission.imported_total_cents ?? 0)}
            {submission.imported_range_label ? ` (${submission.imported_range_label})` : ''} comes
            off this PCO&rsquo;s submitted-to-date and off the project totals. It has no weeks,
            PDFs or sent email behind it, so nothing else changes — but this can&rsquo;t be
            undone. Re-enter it from &ldquo;Add PCO → Historical Data&rdquo; if you remove the
            wrong one.
          </>
        }
        confirmLabel="Remove row"
        busyLabel="Removing…"
        busy={busy}
        destructive
        onConfirm={() => removeSubmission('ledger')}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* Same house confirm, different stakes — this one takes no money with
          it, so the copy says what it frees up (the number) and what to do
          instead if the package was meant to collect work. */}
      <ConfirmModal
        open={confirmRemoveEmpty}
        title="Remove this empty package?"
        body={
          <>
            {formatSubmissionLabel(pcoParentNumber, submission.submission_number)} holds no weeks
            and has never been sent, so nothing billed comes off this PCO. Its number goes back to
            being free — you can use it for a historical row, or for the next package. If you
            meant to bill work here instead, save a block to this PCO and it will land in the
            package that&rsquo;s collecting.
          </>
        }
        confirmLabel="Remove package"
        busyLabel="Removing…"
        busy={busy}
        destructive
        onConfirm={() => removeSubmission('empty')}
        onCancel={() => setConfirmRemoveEmpty(false)}
      />
    </div>
  );
}

export type { RollupListItem };
